import { z } from 'zod';

/**
 * The accounting month, which is the unit a shop owner actually thinks in.
 *
 * A profit figure is only meaningful against a period, and the period has to be the SHOP's,
 * not the server's: a rent payment recorded at 11pm on 31 August in Coimbatore belongs to
 * August, and a server in UTC would file it under September. India has one timezone and no
 * daylight saving, so the boundary is a fixed +05:30 offset — the same arithmetic
 * `shopDayBounds` uses for a day, applied to a month.
 *
 * The month itself travels as `YYYY-MM`. It is what `<input type="month">` produces, it sorts
 * lexically, and it cannot be misread the way `08/2026` can.
 */

export const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/** A calendar month in the shop's own timezone, as it crosses the wire. */
export const monthSchema = z.string().regex(MONTH_PATTERN, 'Enter a month as YYYY-MM');

/** The shop's timezone. India has one, and it never shifts. */
export const SHOP_TIME_ZONE = 'Asia/Kolkata';
export const SHOP_UTC_OFFSET = '+05:30';

function parts(month: string): { year: number; monthIndex: number } {
  const [year, monthNumber] = month.split('-');
  return { year: Number(year), monthIndex: Number(monthNumber) - 1 };
}

/** The month a moment falls in, read in the shop's timezone: `2026-08`. */
export function monthOf(date: Date, timeZone: string = SHOP_TIME_ZONE): string {
  const formatted = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);
  const get = (type: string): string => formatted.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}`;
}

/**
 * Midnight at the start of the month (inclusive) and midnight at the start of the next one
 * (exclusive), both in the shop's timezone. Every period query in the P&L is `>= start` and
 * `< end`, so no entry can fall in two months or in none.
 */
export function monthBounds(month: string): { start: Date; end: Date } {
  if (!MONTH_PATTERN.test(month)) {
    throw new RangeError(`Not a valid month: "${month}". Expected YYYY-MM.`);
  }
  const { year, monthIndex } = parts(month);
  const start = new Date(`${month}-01T00:00:00${SHOP_UTC_OFFSET}`);
  const nextYear = monthIndex === 11 ? year + 1 : year;
  const nextMonthIndex = monthIndex === 11 ? 0 : monthIndex + 1;
  const end = new Date(
    `${String(nextYear).padStart(4, '0')}-${String(nextMonthIndex + 1).padStart(2, '0')}-01T00:00:00${SHOP_UTC_OFFSET}`,
  );
  return { start, end };
}

/** The month before this one — what a month-on-month comparison is drawn against. */
export function previousMonth(month: string): string {
  const { year, monthIndex } = parts(month);
  const previousIndex = monthIndex === 0 ? 11 : monthIndex - 1;
  const previousYear = monthIndex === 0 ? year - 1 : year;
  return `${String(previousYear).padStart(4, '0')}-${String(previousIndex + 1).padStart(2, '0')}`;
}

export function nextMonth(month: string): string {
  const { year, monthIndex } = parts(month);
  const nextIndex = monthIndex === 11 ? 0 : monthIndex + 1;
  const year2 = monthIndex === 11 ? year + 1 : year;
  return `${String(year2).padStart(4, '0')}-${String(nextIndex + 1).padStart(2, '0')}`;
}

/** How many days a month has, so a "rent on the 31st" rule can be clamped to February. */
export function daysInMonth(month: string): number {
  const { year, monthIndex } = parts(month);
  // Day 0 of the following month is the last day of this one.
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/**
 * The shop's calendar DAY, anchored at UTC midnight.
 *
 * An expense is dated to a day, not to an instant — a shop writes "rent, 5 August" and means the
 * day. Two things have to be true of how that is stored, and this is the only anchor that gives
 * both:
 *
 *  - the day is read in the SHOP's timezone, so a bill typed at 12:30am on 1 August in Chennai
 *    is August's and not July's, which is what a server keeping UTC would have made it;
 *  - the stored instant's own ISO string starts with that same date, so `slice(0, 10)` in a CSV
 *    row or a date input shows the day the shop typed. Anchoring at IST midnight instead would
 *    store `2026-08-04T18:30:00Z` for 5 August and print the 4th in every export.
 *
 * UTC midnight of a shop day always falls strictly inside that day's IST month window
 * (`[1st 00:00+05:30, next 1st 00:00+05:30)`), so month bucketing is unaffected.
 */
export function shopDateOnly(date: Date, timeZone: string = SHOP_TIME_ZONE): Date {
  return new Date(`${shopDateInputValue(date, timeZone)}T00:00:00.000Z`);
}

/** The `YYYY-MM-DD` a date input shows, in the shop's timezone. */
export function shopDateInputValue(date: Date, timeZone: string = SHOP_TIME_ZONE): string {
  const formatted = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string): string => formatted.find((part) => part.type === type)?.value ?? '01';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
